import { useMutation } from '@vue/apollo-composable';
import { useToast } from 'primevue/usetoast';
import { useInitiatePaymentMutation, useSetPaymentProviderMutation } from '@/types/vue-apollo-helpers';
import { TOGGLE_PAYMENT_PROVIDER } from '@/graphql/mutations/payment';
import type { InitiatePaymentInput, PaymentProviderInput, PaymentProviderType } from '@/types/composition-functions';
import { toUserMessage } from '@/utils/error';
import {
  providerLabel,
  providerSaveErrorDetail,
  providerToggleToast,
} from '@/composables/billing/paymentProviderSetup';

/** Shown when a payment could not be started; nothing has been charged at this point. */
export const PAYMENT_START_FAILED = 'We couldn\'t start your payment. No money was taken. Please try again.';

export function usePaymentMutations() {
  const toast = useToast();

  const { mutate: initiatePaymentMutate } = useInitiatePaymentMutation();
  const { mutate: setPaymentProviderMutate } = useSetPaymentProviderMutation();
  const { mutate: toggleProviderMutate, loading: togglingProvider } = useMutation(TOGGLE_PAYMENT_PROVIDER);

  async function initiatePayment(input: InitiatePaymentInput) {
    try {
      const { data } = (await initiatePaymentMutate({
        input,
      }))!;
      if (input.processor === 'INTERSWITCH' &&
        (!data?.initiatePayment?.merchantCode?.trim() || !data.initiatePayment.payItemId?.trim())) {
        throw new Error('The Interswitch payment configuration is incomplete. Please contact the school.');
      }
      toast.add({ severity: 'info', summary: 'Taking you to the payment page…', life: 3000 });
      return data?.initiatePayment;
    } catch (error) {
      toast.add({
        severity: 'error',
        summary: 'Couldn\'t start your payment',
        detail: toUserMessage(error, PAYMENT_START_FAILED),
        life: 7000,
      });
      throw error;
    }
  }

  async function setPaymentProvider(input: PaymentProviderInput) {
    const label = providerLabel(input.name);
    try {
      const { data } = (await setPaymentProviderMutate({
        input,
      }))!;
      toast.add({ severity: 'success', summary: `${label} saved`, detail: `Your ${label} details are saved.`, life: 4000 });
      return data?.setPaymentProvider;
    } catch (error) {
      toast.add({ severity: 'error', summary: `Couldn't save ${label}`, detail: providerSaveErrorDetail(error), life: 7000 });
      throw error;
    }
  }

  /**
   * Switches a provider on or off. With the provider name the switch moves at
   * once (optimistic) and snaps back if the server refuses.
   */
  async function togglePaymentProvider(id: string, isActive: boolean, name?: PaymentProviderType | string | null) {
    const label = providerLabel(name);
    try {
      const { data } = (await toggleProviderMutate(
        { id, is_active: isActive },
        name
          ? { optimisticResponse: { togglePaymentProvider: { __typename: 'PaymentProvider', id, name, is_active: isActive } } }
          : {},
      ))!;
      toast.add({ severity: 'success', ...providerToggleToast(name ?? label, isActive), life: 4000 });
      return data?.togglePaymentProvider;
    } catch (error) {
      toast.add({
        severity: 'error',
        summary: `Couldn't switch ${label} ${isActive ? 'on' : 'off'}`,
        detail: toUserMessage(error, 'Nothing was changed. Please try again.'),
        life: 7000,
      });
      throw error;
    }
  }

  return {
    initiatePayment,
    setPaymentProvider,
    togglePaymentProvider,
    togglingProvider,
  };
}
